/**
 * Custom JavaScript
 * ==================
 * This file contains custom JavaScript functionality for the theme.
 * File: assets/custom.js
 * 
 * Table of Contents:
 * 1. Delivery Time Checker Widget
 * 2. Promotional Offer Block
 */

/* =============================================================================
   1. DELIVERY TIME CHECKER WIDGET
   ============================================================================= */

/**
 * Delivery Time Checker
 * A pincode-based delivery availability checker that reads from a CSV file.
 *
 * Usage: Initialize via Alpine.js component in delivery-time-checker.liquid
 */

/* ─── WMS Inventory Configuration ────────────────────────────────────────────
 *
 * Edit these two constants to configure the WMS integration.
 * No other code changes are needed when onboarding new warehouses.
 * ─────────────────────────────────────────────────────────────────────────── */

/**
 * ElasticRun WMS Inventory proxy URL.
 * The proxy handles authentication and CORS.
 */
const _WMS_API_URL = 'https://elasticrun-proxy.rathod6085.workers.dev/';

/**
 * Maps lowercase city keywords (found inside the CSV "Estimated Day" string)
 * to the exact warehouse names returned by the ElasticRun WMS API.
 *
 * How it works:
 *   CSV row: "560001, Express Delivery In Bangalore"
 *   → keyword "bangalore" matches → warehouse "DS-MCF_BLR_Neelmanagala - ER"
 *   → system checks WMS stock at that warehouse for the current variant SKU
 *
 * Rules:
 *   - Keys must be lowercase.
 *   - Values must exactly match the `warehouse` field in the API response.
 *   - If no keyword matches, WMS check is skipped and the metafield
 *     shipping_message is shown as-is (safe fallback).
 *
 * Add a new entry for every warehouse you onboard.
 */
const _WMS_WAREHOUSE_MAP = {
    'bangalore': 'DS-MCF_BLR_Neelmanagala - ER',
    'bhiwandi': 'SW_MCF-Bhiwandi - ER',
    'mumbai': 'SW_MCF-Bhiwandi - ER',
    'sonale': 'SW-MCF-Sonale - ER',
    // Add new city → warehouse entries here, e.g.:
    // 'hyderabad': 'SW-MCF-Hyderabad - ER',
};

document.addEventListener('alpine:init', () => {
    // Prevent duplicate registration
    if (Alpine.data && typeof Alpine.data === 'function') {
        // Check if already registered
        if (window._deliveryCheckerRegistered) return;
        window._deliveryCheckerRegistered = true;

        Alpine.data('xDeliveryChecker', (csvUrl, errorMsg, notAvailableMsg, successPrefix) => ({
            // State
            pincode: '',
            loading: false,
            showResult: false,
            resultMessage: '',
            isError: false,

            // Cache
            csvData: null,
            csvLoaded: false,

            // Stores the last successful CSV result for re-evaluation on variant change
            _lastCsvResult: null,
            _stripObserver: null,

            // WMS inventory cache — populated once per page load, keyed by warehouse then SKU
            _wmsInventory: null,
            _wmsFetchPromise: null,

            // Config
            errorMsg: errorMsg || 'Please enter a valid 6-digit pincode',
            notAvailableMsg: notAvailableMsg || 'Sorry, delivery is not available to this pincode',
            successPrefix: successPrefix || 'Delivery available:',
            csvUrl: csvUrl,

            /**
             * Alpine lifecycle: init
             * Sets up a MutationObserver to watch the shipping strip for variant changes.
             * When _renderDestination() swaps the strip innerHTML on variant switch,
             * we automatically re-evaluate the last pincode result.
             */
            init() {
                // Sync variant SKU/availability from the hidden data carrier to outer div attributes
                // on initial page load (before any variant swap has occurred).
                const strip = document.querySelector('.variant-shipping-message-strip');
                this._syncStripVariantData(strip);
                this._observeShippingStrip();
            },

            /**
             * Watch for variant-shipping-message-* DOM mutations.
             * Uses the section ID embedded in the delivery-checker container's ID:
             *   delivery-checker-{blockId}  →  variant-shipping-message-{sectionId}
             * We locate the strip by its known class instead of ID since we don't
             * have the sectionId directly here.
             */
            _observeShippingStrip() {
                const strip = document.querySelector('.variant-shipping-message-strip');
                if (!strip) return;

                this._stripObserver = new MutationObserver(() => {
                    // This fires when _renderDestination() swaps innerHTML on variant change.
                    // _safeSetHTML() disconnects the observer before OUR own writes,
                    // so this callback only fires for genuine variant switches.

                    // Re-read express state from the NEW inner DOM (data attributes are stale
                    // because _renderDestination only swaps innerHTML, not attributes).
                    const innerStrip = strip.querySelector('.vsm-strip');
                    const msgEl = innerStrip ? innerStrip.querySelector('.vsm-message') : null;
                    const nowCapable = !!innerStrip;
                    const nowMessage = msgEl ? msgEl.textContent.trim() : '';

                    // Sync outer-div data attributes from the new innerHTML
                    strip.dataset.expressCapable = nowCapable ? 'true' : 'false';
                    strip.dataset.expressMessage = nowMessage;

                    // Sync variant SKU + Shopify availability from the hidden data carrier
                    this._syncStripVariantData(strip);

                    // Re-evaluate last pincode result against the new variant
                    if (this._lastCsvResult !== null) {
                        // Fire-and-forget: _applyVariantLogic is async but the WMS
                        // inventory is already cached after the first pincode check.
                        this._applyVariantLogic(this._lastCsvResult);
                    }
                });

                this._stripObserver.observe(strip, { childList: true, subtree: false });
            },

            /**
             * Read the hidden `.vsm-variant-data` span (inside the strip's innerHTML)
             * and sync its values to the outer div as data attributes.
             *
             * Called on init (initial page load) and after every variant swap
             * (MutationObserver callback). This bridges the gap between
             * _renderDestination() only swapping innerHTML and our needing
             * up-to-date SKU/availability on the outer element for _getStripState().
             *
             * @param {HTMLElement|null} strip - The .variant-shipping-message-strip element
             */
            _syncStripVariantData(strip) {
                if (!strip) return;
                const dataEl = strip.querySelector('.vsm-variant-data');
                if (!dataEl) return;
                strip.dataset.variantSku = dataEl.dataset.sku || '';
                strip.dataset.variantAvailable = dataEl.dataset.available || 'false';
            },

            /**
             * Safely update strip innerHTML WITHOUT triggering the MutationObserver.
             * Disconnects before write, reconnects after — prevents the infinite loop
             * caused by the observer firing on our own innerHTML mutations.
             */
            _safeSetHTML(strip, html) {
                if (!strip) return;
                if (this._stripObserver) this._stripObserver.disconnect();
                strip.innerHTML = html;
                // Reconnect immediately — our mutation already happened while disconnected
                if (this._stripObserver) {
                    this._stripObserver.observe(strip, { childList: true, subtree: false });
                }
            },

            /**
             * Validate pincode format
             * @param {string} pincode - The pincode to validate
             * @returns {boolean} - True if valid 6-digit pincode
             */
            validatePincode(pincode) {
                const trimmed = String(pincode).trim();
                return /^[0-9]{6}$/.test(trimmed);
            },

            /**
             * Parse CSV text into a key-value object
             * @param {string} csvText - Raw CSV text content
             * @returns {Object} - Object with pincode as key and delivery time as value
             */
            parseCSV(csvText) {
                const lines = csvText.trim().split('\n');
                const data = {};

                // Skip header row (index 0)
                for (let i = 1; i < lines.length; i++) {
                    const line = lines[i].trim();
                    if (!line) continue;

                    // Auto-detect delimiter.
                    // Tab (TSV) is the default export format from Excel and Google Sheets.
                    // Comma (CSV) is used if the file was saved as comma-separated.
                    // Tab takes priority because some delivery messages contain commas.
                    const delimiter = line.includes('\t') ? '\t' : ',';
                    const parts = line.split(delimiter);

                    if (parts.length >= 2) {
                        // Strip surrounding quotes (added by some spreadsheet apps)
                        const zipcode = parts[0].trim().replace(/^"|"$/g, '');
                        const estimatedDay = parts.slice(1).join(delimiter).trim().replace(/^"|"$/g, '');

                        if (zipcode && estimatedDay) {
                            data[zipcode] = estimatedDay;
                        }
                    }
                }

                return data;
            },

            /**
             * Fetch and cache CSV data
             * @returns {Promise<Object>} - Parsed CSV data object
             */
            async fetchCSV() {
                // Return cached data if available
                if (this.csvLoaded && this.csvData) {
                    return this.csvData;
                }

                // Validate CSV URL
                if (!this.csvUrl) {
                    throw new Error('CSV URL not configured');
                }

                try {
                    const response = await fetch(this.csvUrl, {
                        method: 'GET',
                        headers: {
                            'Accept': 'text/csv, text/plain, */*'
                        }
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to load delivery data: ${response.status}`);
                    }

                    const csvText = await response.text();
                    this.csvData = this.parseCSV(csvText);
                    this.csvLoaded = true;

                    return this.csvData;
                } catch (error) {
                    console.error('[DeliveryChecker] Error fetching CSV:', error);
                    throw error;
                }
            },

            /**
             * Read the shipping strip state for the current variant.
             * Returns { isExpressCapable, expressMessage }
             */
            _getStripState() {
                const strip = document.querySelector('.variant-shipping-message-strip');
                if (!strip) return {
                    isExpressCapable: false,
                    expressMessage: '',
                    variantSku: '',
                    variantAvailable: false
                };
                return {
                    isExpressCapable: strip.dataset.expressCapable === 'true',
                    expressMessage: strip.dataset.expressMessage || '',
                    variantSku: strip.dataset.variantSku || '',
                    variantAvailable: strip.dataset.variantAvailable === 'true'
                };
            },

            /**
             * Fetch the full WMS inventory from the ElasticRun proxy and cache it.
             *
             * Returns a nested Map for O(1) lookups:
             *   Map<warehouseName, Map<sku, available_qty>>
             *
             * Only one HTTP request is made per page load. Concurrent callers
             * share the same in-flight Promise via _wmsFetchPromise.
             *
             * On any error (network failure, bad JSON, etc.) returns null so
             * the caller can silently fall back to the metafield message.
             *
             * @returns {Promise<Map|null>}
             */
            async _fetchWmsInventory() {
                // Return cached result immediately after first successful fetch
                if (this._wmsInventory) return this._wmsInventory;

                // Deduplicate concurrent requests (e.g. rapid variant switches)
                if (this._wmsFetchPromise) return this._wmsFetchPromise;

                this._wmsFetchPromise = fetch(_WMS_API_URL)
                    .then(res => {
                        if (!res.ok) throw new Error(`WMS API error: ${res.status}`);
                        return res.json();
                    })
                    .then(data => {
                        // API structure: data.message.response_json.message.rows[]
                        const rows = data?.message?.response_json?.message?.rows;
                        if (!Array.isArray(rows)) throw new Error('Unexpected WMS response shape');

                        // Build nested Map: warehouse → sku → available_qty
                        const inventory = new Map();
                        for (const row of rows) {
                            if (!row.warehouse || !row.sku) continue;
                            if (!inventory.has(row.warehouse)) {
                                inventory.set(row.warehouse, new Map());
                            }
                            const normSku = String(row.sku).trim().toLowerCase();
                            inventory.get(row.warehouse).set(normSku, row.available_qty ?? 0);
                        }

                        this._wmsInventory = inventory;
                        this._wmsFetchPromise = null;
                        return inventory;
                    })
                    .catch(err => {
                        console.warn('[DeliveryChecker] WMS inventory fetch failed — falling back to metafield:', err);
                        this._wmsFetchPromise = null;
                        return null; // safe: callers treat null as "unknown stock"
                    });

                return this._wmsFetchPromise;
            },

            /**
             * Resolve which WMS warehouse serves a pincode, using the CSV
             * delivery message as the signal.
             *
             * Strategy: scan _WMS_WAREHOUSE_MAP for a keyword that appears
             * (case-insensitively) in the csvResult string. First match wins.
             *
             * Example:
             *   csvResult = "Express Delivery In Bangalore"
             *   → matches "bangalore" → returns "DS-MCF_BLR_Neelmanagala - ER"
             *
             * Returns null if no keyword matches (unknown city/warehouse).
             *
             * @param {string} csvResult - The raw CSV estimated-day string
             * @returns {string|null} Exact warehouse name or null
             */
            _getWarehouseForCsvResult(csvResult) {
                if (!csvResult) return null;
                const lower = csvResult.toLowerCase();
                for (const [keyword, warehouseName] of Object.entries(_WMS_WAREHOUSE_MAP)) {
                    if (lower.includes(keyword)) return warehouseName;
                }
                return null;
            },

            /**
             * Apply cross-logic between pincode CSV result, variant state, and WMS stock.
             *
             * This is now async to support the WMS inventory check.
             * It is called from both checkDelivery() (awaited) and the
             * MutationObserver callback (fire-and-forget — safe because the WMS
             * inventory is already cached after the first pincode check).
             *
             * Decision tree:
             * ┌─ Variant NOT express-capable
             * │    └─ Show CSV delivery estimate in the result div (unchanged).
             * └─ Variant IS express-capable
             *      ├─ CSV result NOT express → show non-express warning in strip (unchanged).
             *      ├─ CSV result IS express
             *      │    ├─ Resolve warehouse from CSV message keywords.
             *      │    ├─ Fetch WMS inventory (cached after first call).
             *      │    ├─ Variant unavailable in Shopify  → fallback (no Next Day Delivery).
             *      │    ├─ Warehouse stock available_qty > 0  → show express / Next Day Delivery.
             *      │    └─ Warehouse stock == 0 OR unknown OR API failed → fallback to metafield message.
             *      └─ CSV result null → not-available notice in strip (unchanged).
             *
             * @param {string|null} csvResult - The CSV estimated day value, or null if not found
             */
            async _applyVariantLogic(csvResult) {
                const { isExpressCapable, expressMessage, variantSku, variantAvailable } = this._getStripState();

                // ── Non-express variant path (unchanged) ──────────────────────────────────
                if (!isExpressCapable) {
                    if (csvResult !== null) {
                        this.isError = false;
                        if (csvResult.toLowerCase().includes('express')) {
                            // Variant does not support express — show standard fallback time
                            this.resultMessage = `${this.successPrefix} 2 to 3 Days`;
                        } else {
                            // Normal delivery estimate from CSV
                            this.resultMessage = `${this.successPrefix} ${csvResult}`;
                        }
                    } else {
                        this.isError = true;
                        this.resultMessage = this.notAvailableMsg;
                    }
                    this.showResult = true;
                    return;
                }

                // ── Express-capable variant path ──────────────────────────────────────────
                const strip = document.querySelector('.variant-shipping-message-strip');

                if (csvResult !== null && !csvResult.toLowerCase().includes('express')) {
                    // Pincode is serviceable but NOT by express → show standard warning
                    this._showNonExpressWarning(strip, csvResult);
                    this.isError = false;
                    this.resultMessage = `${this.successPrefix} ${csvResult}`;
                    this.showResult = true;
                    return;
                }

                if (csvResult === null) {
                    // Pincode not found in CSV → not-available notice
                    this._showNonExpressWarning(strip, null);
                    this.isError = true;
                    this.resultMessage = this.notAvailableMsg;
                    this.showResult = true;
                    return;
                }

                // ── csvResult IS express — apply WMS stock check ──────────────────────────
                //
                // Step 1: Shopify availability gate.
                // Variant is sold out in Shopify — never promise express delivery.
                if (!variantAvailable) {
                    this._showNonExpressWarning(strip, null, 'This variant is currently out of stock.');
                    this.isError = true;
                    this.resultMessage = 'This variant is currently out of stock.';
                    this.showResult = true;
                    return;
                }

                // Step 2: Resolve which warehouse serves this pincode.
                const targetWarehouse = this._getWarehouseForCsvResult(csvResult);

                if (!targetWarehouse) {
                    // Unknown city / no warehouse mapping configured → safe fallback to metafield msg
                    console.warn('[DeliveryChecker] No warehouse mapping found for CSV result:', csvResult);
                    this._restoreExpressStrip(strip, expressMessage);
                    this.isError = false;
                    this.resultMessage = `${this.successPrefix} ${csvResult}`;
                    this.showResult = true;
                    return;
                }

                // Step 3: Fetch WMS inventory (cached — only one HTTP request per page load).
                const inventory = await this._fetchWmsInventory();

                if (!inventory) {
                    // API failure → silent fallback to existing metafield message (Scenario 5)
                    this._restoreExpressStrip(strip, expressMessage);
                    this.isError = false;
                    this.resultMessage = `${this.successPrefix} ${csvResult}`;
                    this.showResult = true;
                    return;
                }

                // Step 4: Look up available_qty for the target warehouse + current variant SKU.
                const warehouseStock = inventory.get(targetWarehouse);
                const normVariantSku = String(variantSku).trim().toLowerCase();
                const rawQty = warehouseStock ? warehouseStock.get(normVariantSku) : undefined;

                if (rawQty === undefined) {
                    // State (c): SKU not tracked by WMS at all.
                    // ElasticRun has no record of this product (e.g. not yet synced).
                    // We cannot make a stock decision → trust the merchant metafield value (expressMessage).
                    console.info('[DeliveryChecker] SKU not in WMS, trusting metafield for:', variantSku);
                    this._restoreExpressStrip(strip, expressMessage);
                    this.isError = false;
                    this.resultMessage = `${this.successPrefix} ${csvResult}`;
                    this.showResult = true;
                } else if (rawQty > 0) {
                    // State (a): WMS confirms stock at the pincode's warehouse → show Next Day Delivery.
                    const matchMsg = `EXPRESS DELIVERY AVAILABLE ON ${this.pincode} PINCODE. CLICK TO ORDER.`;
                    this._restoreExpressStrip(strip, matchMsg);
                    this.isError = false;
                    this.resultMessage = `${this.successPrefix} ${csvResult}`;
                    this.showResult = true;
                } else {
                    // State (b): WMS explicitly shows available_qty = 0 at the pincode's warehouse.
                    // Show yellow warning strip and widget error/warning.
                    const zeroStockMsg = 'Express delivery is not available for your pincode at this time. Please check back later.';
                    this._showNonExpressWarning(strip, null, zeroStockMsg);
                    this.isError = true;
                    this.resultMessage = zeroStockMsg;
                    this.showResult = true;
                }
            },

            /**
             * Restore the strip to its original express state.
             * Called when the pincode IS express-compatible with this variant.
             */
            _restoreExpressStrip(strip, expressMessage) {
                this._safeSetHTML(strip, `<div class="vsm-strip vsm-strip--express"><span class="vsm-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18"><rect x="1" y="3" width="15" height="13" rx="1"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg></span><span class="vsm-message">${expressMessage}</span></div>`);
            },

            /**
             * Override the strip with a non-express notice.
             * Called when the variant supports express but:
             *   - the pincode is not express-serviceable, OR
             *   - the WMS warehouse has zero stock, OR
             *   - the Shopify variant is sold out.
             *
             * @param {HTMLElement} strip - The strip wrapper element
             * @param {string|null} csvResult - Delivery estimate from CSV, or null if not found
             * @param {string} [overrideMsg] - Optional custom message to show instead of default
             */
            _showNonExpressWarning(strip, csvResult, overrideMsg) {
                let deliveryLine;
                if (overrideMsg) {
                    deliveryLine = overrideMsg;
                } else if (csvResult) {
                    deliveryLine = `Delivery will take: <strong>${csvResult}</strong>`;
                } else {
                    deliveryLine = 'Delivery availability could not be confirmed for this pincode.';
                }
                this._safeSetHTML(strip, `<div class="vsm-strip vsm-strip--warning"><span class="vsm-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg></span><span class="vsm-message vsm-message--warning">This pincode is not available for Express Delivery. ${deliveryLine}</span></div>`);
            },

            /**
             * Main check delivery function
             * Validates pincode and checks against CSV data
             */
            async checkDelivery() {
                // Reset previous state
                this.showResult = false;
                this.isError = false;
                this.resultMessage = '';
                this._lastCsvResult = null;

                // Validate pincode format
                if (!this.validatePincode(this.pincode)) {
                    this.isError = true;
                    this.resultMessage = this.errorMsg;
                    this.showResult = true;
                    return;
                }

                // Start loading
                this.loading = true;

                try {
                    // Fetch and parse CSV data
                    const data = await this.fetchCSV();

                    // Normalize pincode
                    const trimmedPincode = String(this.pincode).trim();

                    // Store result for re-evaluation when variant changes
                    this._lastCsvResult = data[trimmedPincode] || null;

                    // Apply variant-aware logic.
                    // Awaited so any async error (WMS fetch, DOM access) is caught
                    // by the outer try/catch and surfaced cleanly to the user.
                    await this._applyVariantLogic(this._lastCsvResult);

                } catch (error) {
                    this.isError = true;
                    this.resultMessage = 'Unable to check delivery. Please try again.';
                    this._lastCsvResult = null;
                    console.error('[DeliveryChecker] Check failed:', error);
                    this.showResult = true;
                } finally {
                    this.loading = false;
                }
            },

            /**
             * Clear the current result and reset the shipping strip to its natural state.
             * Called when the user clears/resets the pincode field.
             */
            clearResult() {
                this.showResult = false;
                this.resultMessage = '';
                this.isError = false;
                this._lastCsvResult = null;

                // Restore original strip state by triggering a soft re-render via URL
                // The strip already has the correct server-rendered content from page load —
                // we only need to reset if it was overridden by _showNonExpressWarning.
                const strip = document.querySelector('.variant-shipping-message-strip');
                if (strip && strip.dataset.expressCapable === 'true') {
                    const msg = strip.dataset.expressMessage || '';
                    this._restoreExpressStrip(strip, msg);
                }
            },

            /**
             * Handle pincode input - only allow numbers
             */
            handleInput(event) {
                this.pincode = event.target.value.replace(/\D/g, '').slice(0, 6);
            },

            /**
             * Cleanup MutationObserver on Alpine destroy
             */
            destroy() {
                if (this._stripObserver) {
                    this._stripObserver.disconnect();
                    this._stripObserver = null;
                }
            }
        }));

    }
});

/* =============================================================================
   2. PROMOTIONAL OFFER BLOCK
   ============================================================================= */

/**
 * Promotional Offer Block
 * Displays discount offers with countdown timer and details popup.
 * 
 * Usage: Initialize via Alpine.js component in promo-offer-block.liquid
 */

document.addEventListener('alpine:init', () => {
    // Prevent duplicate registration
    if (Alpine.data && typeof Alpine.data === 'function') {
        // Check if already registered
        if (window._promoOfferRegistered) return;
        window._promoOfferRegistered = true;

        Alpine.data('xPromoOffer', (endDateStr, showTimer) => ({
            // State
            popupOpen: false,
            timerActive: false,
            timeDisplay: '00:00:00',
            timerInterval: null,

            // Config
            endDate: null,
            showTimer: showTimer,

            /**
             * Initialize the component
             */
            init() {
                if (this.showTimer && endDateStr) {
                    this.endDate = new Date(endDateStr);
                    this.startTimer();
                }
            },

            /**
             * Start the countdown timer
             */
            startTimer() {
                if (!this.endDate || isNaN(this.endDate.getTime())) {
                    this.timerActive = false;
                    return;
                }

                // Initial update
                this.updateTimer();

                // Update every second
                this.timerInterval = setInterval(() => {
                    this.updateTimer();
                }, 1000);

                this.timerActive = true;
            },

            /**
             * Update the timer display
             */
            updateTimer() {
                const now = new Date();
                const diff = this.endDate - now;

                if (diff <= 0) {
                    this.timerActive = false;
                    this.timeDisplay = '00:00:00';
                    if (this.timerInterval) {
                        clearInterval(this.timerInterval);
                        this.timerInterval = null;
                    }
                    return;
                }

                const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((diff % (1000 * 60)) / 1000);

                // Format display based on remaining time
                if (days > 0) {
                    this.timeDisplay = `${days}d ${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`;
                } else {
                    this.timeDisplay = `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`;
                }
            },

            /**
             * Pad number with leading zero
             */
            pad(num) {
                return num.toString().padStart(2, '0');
            },

            /**
             * Open the details popup
             */
            openPopup() {
                this.popupOpen = true;
                document.body.style.overflow = 'hidden';
            },

            /**
             * Close the details popup
             */
            closePopup() {
                this.popupOpen = false;
                document.body.style.overflow = '';
            },

            /**
             * Cleanup on destroy
             */
            destroy() {
                if (this.timerInterval) {
                    clearInterval(this.timerInterval);
                }
            }
        }));
    }
});
